import React, { useEffect, useState, useRef, useMemo } from "react";
import { add_sizes, get_sizes, edit_sizes, delete_size, get_category } from "../../reduxData/user/userAction";
import { useDispatch, useSelector } from "react-redux";
import CustomPagination from "../../components/CustomPagination";
import { useTranslation } from "react-i18next";
import { languageFields } from "../../hooks/LanguageFields";

const addBodyClass = () => document.body.classList.add("overflow-hidden");
const removeBodyClass = () => document.body.classList.remove("overflow-hidden");

const Chip = ({ label, onRemove }) => (
    <span className="sz-chip">
        {label}
        {onRemove && <button className="sz-chip-x" onClick={onRemove}>✕</button>}
    </span>
);

const TableChip = ({ label }) => <span className="sz-chip-table">{label}</span>;

const CategorySearchDropdown = ({
    categories = [],
    value,
    onChange,
    placeholder = "Search or select category",
    disabled = false
}) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const selected = categories.find((c) => c._id === value || c.id === value);

    const filteredCategories = useMemo(() => {
        return categories.filter((c) =>
            c.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [categories, search]);

    useEffect(() => {
        const handleOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                if (selected) setSearch(selected.name);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [selected]);

    useEffect(() => {
        if (selected) setSearch(selected.name);
        else setSearch("");
    }, [selected]);

    return (
        <div className="category-select-wrapper" ref={wrapperRef}>
            <div
                className={`category-select-input-box ${open ? "active" : ""} ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && setOpen(true)}
            >
                <i className="fas fa-search search-icon"></i>

                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    placeholder={placeholder}
                    className="category-select-input"
                    onFocus={() => !disabled && setOpen(true)}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        if (!disabled) setOpen(true);
                    }}
                    disabled={disabled}
                    readOnly={disabled}
                />

                <i className={`fas fa-chevron-down dropdown-arrow ${open ? "rotate" : ""}`}></i>
            </div>

            {open && !disabled && (
                <div className="category-dropdown-menu">
                    {filteredCategories.length === 0 ? (
                        <div className="category-empty">{t("sizes.noCategoryFound")}</div>
                    ) : (
                        filteredCategories.map((c) => {
                            const id = c._id || c.id;
                            const isActive = value === id;
                            return (
                                <div
                                    key={id}
                                    className={`category-item ${isActive ? "selected" : ""}`}
                                    onClick={() => {
                                        onChange(id);
                                        setSearch(c.name);
                                        setOpen(false);
                                    }}
                                >
                                    <span>
                                        {languageFields(c, "name", i18n)}
                                        {/* {c.name} */}
                                    </span>
                                    {isActive && <i className="fas fa-check"></i>}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const CreateModal = ({onClose, onSave, categoriesList = []}) => {
    const { t } = useTranslation();
    const [category_id, setCategoryId] = useState("");
    const [subcategory_id, setSubcategoryId] = useState("");
    const [sizeInput, setSizeInput] = useState("");
    const [sizes, setSizes] = useState([]);
    const [errors, setErrors] = useState({
        category: "",
        subcategory: "",
        sizes: "",
    });

    const selectedCategory =
        categoriesList.find(
            (c) =>
                c.id === Number(category_id) ||
                c._id === category_id
        );
    const subcategories =
        selectedCategory?.subcategories || [];

    const addSize = (val) => {
        const v = String(val || "")
            .trim()
            .toUpperCase();
        if (!v || sizes.includes(v)) return;
        setSizes((s) => [...s, v]);
        setSizeInput("");
        setErrors((prev) => ({
            ...prev,
            sizes: "",
        }));
    };

    const removeSize = (val) => {
        const updated = sizes.filter(
            (x) => x !== val
        );
        setSizes(updated);
        setErrors((prev) => ({
            ...prev,
            sizes: updated.length
                ? ""
                : t("sizes.validationSize"),
        }));
    };

    const handleCategoryChange = (val) => {
        setCategoryId(val);
        setSubcategoryId("");
        setErrors((prev) => ({
            ...prev,
            category: val
                ? ""
                : t("sizes.validationCategory"),
            subcategory: "",
        }));
    };

    const handleSubCategoryChange = (val) => {
        setSubcategoryId(val);
        if (category_id) {
            setErrors((prev) => ({
                ...prev,
                subcategory: val
                    ? ""
                    : t("sizes.validationSubCategory"),
            }));
        }
    };

    const validate = () => {
        let valid = true;
        const newErrors = {
            category: "",
            subcategory: "",
            sizes: "",
        };
        if (!category_id) {
            newErrors.category =
                t("sizes.validationCategory");
            valid = false;
        }
        if (category_id && !subcategory_id) {
            newErrors.subcategory =
                t("sizes.validationSubCategory");
            valid = false;
        }
        if (!sizes.length) {
            newErrors.sizes =
                t("sizes.validationSize");
            valid = false;
        }
        setErrors(newErrors);
        return valid;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            category_id: Number(category_id),
            subcategory_id: Number(
                subcategory_id
            ),
            sizes,
        });
    };

    return (
        <div
            className="sz-overlay"
            onClick={(e) =>
                e.target === e.currentTarget &&
                onClose()
            }
        >
            <div className="sz-modal sz-modal-create">
                <div className="sz-modal-header">
                    <h2>{t("sizes.newSizeEntry")}</h2>
                    <button
                        className="sz-modal-close"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>

                <div className="sz-modal-body">
                    <div className="sz-form-grid">
                        <div className="sz-field">
                            <label>{t("sizes.selectCategory")}</label>
                            <CategorySearchDropdown
                                categories={categoriesList}
                                value={category_id}
                                onChange={
                                    handleCategoryChange
                                }
                                placeholder={t("sizes.searchCategory")}
                            />

                            {errors.category && (
                                <div className="error_msg">
                                    {errors.category}
                                </div>
                            )}
                        </div>

                        <div className="sz-field">
                            <label>{t("sizes.selectSubCategory")}</label>
                            <CategorySearchDropdown
                                categories={subcategories}
                                value={subcategory_id}
                                onChange={
                                    handleSubCategoryChange
                                }
                                placeholder={t("sizes.searchSubCategory")}
                                disabled={!category_id}
                            />
                            {errors.subcategory && (
                                <div className="error_msg">
                                    {errors.subcategory}
                                </div>
                            )}
                        </div>
                        <div className="sz-field">
                            <label>{t("sizes.addSizes")}</label>
                            <div className="sz-input-row">
                                <input
                                    className={`sz-input ${errors.sizes
                                        ? "input-error"
                                        : ""
                                        }`}
                                    placeholder={t("sizes.sizePlaceholder")}
                                    value={sizeInput}
                                    onChange={(e) =>
                                        setSizeInput(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === ","
                                        ) {
                                            e.preventDefault();

                                            addSize(sizeInput);
                                        }
                                    }}
                                />

                                <button
                                    className="sz-btn-add"
                                    onClick={() =>
                                        addSize(sizeInput)
                                    }
                                >
                                    {t("sizes.add")}
                                </button>
                            </div>

                            {errors.sizes && (
                                <div className="error_msg">
                                    {errors.sizes}
                                </div>
                            )}

                            {sizes.length > 0 && (
                                <div className="sz-chip-tray">
                                    {sizes.map((s) => (
                                        <Chip
                                            key={s}
                                            label={s}
                                            onRemove={() =>
                                                removeSize(s)
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="sz-form-actions">
                        <button
                            className="sz-btn-ghost"
                            onClick={onClose}
                        >
                            {t("sizes.cancel")}
                        </button>

                        <button
                            className="sz-btn-primary"
                            onClick={handleSave}
                        >
                            {t("sizes.create")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const EditModal = ({ record, onClose, onSizeDeleted, onSizeAdded, onSizeEdited, canCreate, canEdit, canDelete }) => {
    const normalize = (sizesArr = []) => {
        return sizesArr.map((s, idx) => {
            if (!s) return null;
            if (typeof s === "string") return { id: `${record?.id || 'rec'}-${idx}-${s}`, size_text: s };
            return { id: s.id ?? `${record?.id || 'rec'}-${idx}-${s.size_text ?? idx}`, size_text: s.size_text ?? String(s) };
        }).filter(Boolean);
    };

    const { t } = useTranslation();

    const [sizes, setSizes] = useState(normalize(record?.sizes || []));
    const [sizeInput, setSizeInput] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [addError, setAddError] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editInput, setEditInput] = useState("");

    const handleDeleteSize = async (sizeItem) => {
        setDeletingId(sizeItem.id);
        try {
            await onSizeDeleted(record, sizeItem);
            setSizes((prev) => prev.filter((s) => s.id !== sizeItem.id));
        } catch (e) {
            console.error("Delete size failed", e);
        } finally {
            setDeletingId(null);
        }
    };

    const handleAddSize = async () => {
        const v = String(sizeInput || "").trim().toUpperCase();
        if (!v) return;
        const duplicate = sizes.find((s) => s.size_text.toUpperCase() === v);
        if (duplicate) {
            setAddError(`"${v}" already exists.`);
            return;
        }
        setAddError(null);
        setIsAdding(true);
        try {
            const newSize = await onSizeAdded(record, v);
            if (newSize) {
                const ns = typeof newSize === 'string' ? { id: `${record?.id || 'rec'}-${Date.now()}-${newSize}`, size_text: newSize } : (newSize.id ? newSize : { id: newSize.id ?? `${record?.id || 'rec'}-${Date.now()}`, size_text: newSize.size_text ?? String(newSize) });
                setSizes((prev) => [...prev, ns]);
            }
            setSizeInput("");
        } catch (e) {
            console.error("Add size failed", e);
        } finally {
            setIsAdding(false);
        }
    };

    useEffect(() => {
        setSizes(normalize(record?.sizes || []));
    }, [record]);

    const handleEditSize = async (sizeItem) => {
        const v = String(editInput || "").trim().toUpperCase();
        if (!v || v === sizeItem.size_text.toUpperCase()) {
            setEditingId(null);
            return;
        }
        try {
            await onSizeEdited(record, sizeItem, v);
            setSizes(prev =>
                prev.map(s => s.id === sizeItem.id ? { ...s, size_text: v } : s)
            );
        } catch (e) {
            console.error("Edit size failed", e);
        } finally {
            setEditingId(null);
            setEditInput("");
        }
    };

    return (
        <div className="sz-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="sz-modal sz-modal--edit">
                <div className="sz-modal-header">
                    <div className="sz-modal-title-group">
                        <h2>{t("sizes.editModalTitle")}</h2>
                        <span className="sz-type-badge sz-type-badge--header">{record?.subcategory_name}</span>
                    </div>
                    <button className="sz-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="sz-modal-body">
                    {canCreate && (
                        <div className="sz-edit-add-row">
                            <div className="sz-field" style={{ flex: 1 }}>
                                <label>{t("sizes.addNewSize")}</label>
                                <div className="sz-input-row">
                                    <input
                                        className="sz-input"
                                        placeholder={t("sizes.editSizePlaceholder")}
                                        value={sizeInput}
                                        onChange={(e) => { setSizeInput(e.target.value); setAddError(null); }}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleAddSize(); }}
                                        disabled={isAdding}
                                    />
                                    <button className="sz-btn-add" onClick={handleAddSize} disabled={isAdding}>
                                        {isAdding ? t("sizes.adding") : t("sizes.add")}
                                    </button>
                                </div>
                                {addError && <div className="sz-error sz-error--sm">{addError}</div>}
                            </div>
                        </div>
                    )}

                    <div className="sz-edit-sizes-section">
                        <label className="sz-field-label">
                            {t("sizes.existingSizes")}
                            <span className="sz-count" style={{ marginLeft: 8 }}>{sizes.length}</span>
                        </label>

                        {sizes.length === 0 ? (
                            <div className="sz-empty" style={{ padding: "24px 0" }}>
                                <p>{t("sizes.noSizesYet")}</p>
                            </div>
                        ) : (
                            <div className="sz-edit-size-list">
                                {sizes.map((sizeItem) => (
                                    <div key={sizeItem.id} className={`sz-edit-size-row${deletingId === sizeItem.id ? " sz-edit-size-row--deleting" : ""}`}>
                                        {canEdit && editingId === sizeItem.id ? (
                                            <>
                                                <input
                                                    className="sz-input"
                                                    value={editInput}
                                                    autoFocus
                                                    onChange={e => setEditInput(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") handleEditSize(sizeItem);
                                                        if (e.key === "Escape") setEditingId(null);
                                                    }}
                                                    style={{ width: 80 }}
                                                />
                                                <button className="sz-btn-add" onClick={() => handleEditSize(sizeItem)}>{t("sizes.save")}</button>
                                                <button className="sz-btn-ghost" onClick={() => setEditingId(null)}>{t("sizes.cancel")}</button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="sz-chip-table sz-chip-table--lg">{sizeItem.size_text}</span>
                                                {(canEdit || canDelete) && (
                                                    <div className="d-flex gap-2">
                                                        {canEdit && (
                                                            <button className="sz-btn-edit" onClick={() => { setEditingId(sizeItem.id); setEditInput(sizeItem.size_text); }}>{t("sizes.edit")}</button>
                                                        )}
                                                        {canDelete && (
                                                            <button className="sz-btn-del sz-btn-del--sm" onClick={() => handleDeleteSize(sizeItem)} disabled={deletingId === sizeItem.id}>
                                                                {deletingId === sizeItem.id ? "…" : t("sizes.delete")}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="sz-modal-footer">
                    <button className="sz-btn-ghost" onClick={onClose}>{t("sizes.close")}</button>
                </div>
            </div>
        </div>
    );
};

const SizesManagement = () => {
    const { sizesData, totalSizes } = useSelector((state) => state.user);
    const { categoriesList = [] } = useSelector((state) => state.user);
    const { userPermissions } = useSelector((state) => state.auth);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const canCreateSize = userPermissions?.sizes?.create;
    const canEditSize = userPermissions?.sizes?.edit;
    const canDeleteSize = userPermissions?.sizes?.delete;
    const canManageSize = canCreateSize || canEditSize || canDeleteSize;

    const [modal, setModal] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const fetchSizes = (page = currentPage, rows = perPage) => get_sizes(dispatch, page, rows);
    const fetchCategories = () => get_category(dispatch, 1, 100);

    useEffect(() => {
        fetchCategories();
        fetchSizes();
    }, []);

    const handleCreate = async (payload) => {
        const res = await add_sizes(payload, dispatch);
        if (res?.status) {
            fetchSizes();
            fetchCategories();
        }
        setModal(null);
        removeBodyClass();
    };

    const handleSizeDeleted = async (record, sizeItem) => {
        await delete_size(sizeItem.id, dispatch);
        fetchSizes();
    };

    const handleSizeAdded = async (record, sizeText) => {
        const res = await add_sizes(
            {
                category_id: record.category_id,
                subcategory_id: record.subcategory_id,
                sizes: [sizeText],
            },
            dispatch
        );
        fetchSizes();
        if (res?.status && res?.data?.sizes?.length) {
            return {
                id: Date.now(),
                size_text: res.data.sizes[0],
            };
        }
        return null;
    };

    const handleSizeEdited = async (record, sizeItem, newText) => {
        await edit_sizes(sizeItem.id, { size_text: newText }, dispatch);
        fetchSizes();
    };

    return (
        <div className="sz-page">
            <div className="sz-header">
                <div className="sz-header-left">
                    <h1>{t("sizes.pageTitle")}</h1>
                    <p>{t("sizes.pageSubtitle")}</p>
                </div>
                {canCreateSize && (
                    <button className="sz-btn-primary" onClick={() => { setModal({ mode: "create" }); addBodyClass(); }}>
                        {t("sizes.newSizeEntry")}
                    </button>
                )}
            </div>

            <div className="sz-card">
                <div className="sz-table-section">
                    {totalSizes === 0 ? (
                        <div className="sz-empty">
                            <div className="sz-empty-icon">📦</div>
                            <p>No size entries yet. Click <strong>New Size Entry</strong> to get started.</p>
                        </div>
                    ) : (
                        <div className="sz-table-wrap">
                            <table className="sz-table">
                                <thead>
                                    <tr>
                                        <th>{t("sizes.tableNumber")}</th>
                                        <th>{t("sizes.tableCategory")}</th>
                                        <th>{t("sizes.tableSubCategory")}</th>
                                        <th>{t("sizes.tableSizes")}</th>
                                        {canEditSize && <th>{t("sizes.tableActions")}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sizesData.map((rec, idx) => (
                                        <tr key={rec.id}>
                                            <td className="sz-idx">{(currentPage - 1) * perPage + idx + 1}</td>
                                            <td>
                                                <span className="sz-type-badge">
                                                {rec.category_name}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="sz-type-badge">
                                                    {rec.subcategory_name}
                                                    </span>
                                            </td>
                                            <td>
                                                <div className="sz-table-chips">
                                                    {rec.sizes?.map((s, i) => (
                                                        <TableChip key={typeof s === 'string' ? `${rec.id}-${i}-${s}` : s.id ?? `${rec.id}-${i}`} label={typeof s === 'string' ? s : s.size_text} />
                                                    ))}
                                                </div>
                                            </td>
                                            {canEditSize && (
                                                <td>
                                                    <div className="sz-actions">
                                                        <button className="sz-btn-edit" onClick={() => { setModal({ mode: "edit", record: rec }); addBodyClass(); }}>{t("sizes.edit")}</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {totalSizes > 0 && (
                <div className="pagination-wrapper sz-pagination">
                    <CustomPagination
                        total={totalSizes}
                        onPageChange={(page, rows) => {
                            setCurrentPage(page);
                            setPerPage(rows);
                            fetchSizes(page, rows);
                        }}
                    />
                </div>
            )}

            {modal?.mode === "create" && (
                <CreateModal
                    onClose={() => { setModal(null); removeBodyClass(); }}
                    onSave={handleCreate}
                    categoriesList={categoriesList}
                />
            )}

            {modal?.mode === "edit" && (
                <EditModal
                    record={modal.record}
                    onClose={() => { setModal(null); removeBodyClass(); }}
                    onSizeDeleted={handleSizeDeleted}
                    onSizeAdded={handleSizeAdded}
                    onSizeEdited={handleSizeEdited}
                    canCreate={canCreateSize}
                    canEdit={canEditSize}
                    canDelete={canDeleteSize}
                />
            )}
        </div>
    );
};

export default SizesManagement;
